/**
 * Type definitions for PDF.js viewer component
 * Used for migrating from Apryse WebViewer to open-source PDF.js
 */

import { CounterpartyPermissionLevel } from '../../types/counterparty-permissions';

// ============================================================================
// Page Identity System (Two-Phase Tracking)
// ============================================================================

/**
 * PDF Page Reference with stable identity
 * Separates page identity (id) from page position (position)
 *
 * Identity: pageId - stable UUID that never changes
 * Position: position - current index in PDF, changes during reorder
 */
export interface PDFPageRef {
  id: string;        // Permanent UUID - assigned on page creation/load
  position: number;  // Current 1-based index in PDF (transient, derived)
}

export interface PDFRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export type AnnotationType = 'signature' | 'initials' | 'date' | 'text';

export interface AnnotationData {
  id: string;
  canonical_id: string; // Stable identifier for backend tracking
  canonicalId: string;  // Camelcase version for JS compatibility
  type: AnnotationType;
  page: number; // 1-based page number
  rect: PDFRect; // PDF coordinates (bottom-left origin, points)
  signerId?: string;
  signerEmail?: string;
  value?: string; // For text/date fields
  imageData?: string; // For signature/initials (base64 PNG)
  required?: boolean;
  fontSize?: number; // For text fields
  // Original dimensions before WebViewer/browser adjustments
  original_height?: number;
  original_width?: number;
  original_y?: number;
}

export type ViewerMode =
  | 'read-only'      // View executed documents (Phase 1 ✅)
  | 'signing-prep'   // Place signature fields (Phase 2 ✅)
  | 'edit'           // Edit PDF documents - fill forms, add annotations (Phase 3 ⏳)
  | 'signing'        // Fill signature fields (Phase 4)
  | 'review';        // Add comments/annotations (Phase 5)

export type ResourceType = 'document' | 'template';

export interface PDFJSViewerProps {
  pdfUrl: string;
  mode: ViewerMode;
  initialAnnotations?: AnnotationData[];
  onAnnotationsChange?: (annotations: AnnotationData[]) => void;
  onFieldClick?: (field: AnnotationData) => void;
  onPagesReady?: (pagesInfo: PDFPageInfo[]) => void;
  onDocumentLoad?: (pdfDocument: any) => void; // Callback when PDF document is loaded
  onPagesLoad?: (pages: Array<{ id: string; position: number }>) => void; // TWO-PHASE: Callback when pages array updates
  showControls?: boolean;
  className?: string;
  // Mobile-specific props
  enableTouch?: boolean;
  // Zoom limits
  minZoom?: number;
  maxZoom?: number;
  // Show thumbnail sidebar
  showThumbnails?: boolean;
  // Field overlay props (for signing-prep mode)
  fieldOverlayProps?: {
    fields: any[];
    selectedFieldId: string | null;
    onSelectField: (id: string | null) => void;
    onUpdateField: (id: string, updates: any) => void;
    onDeleteField: (id: string) => void;
    readOnlyMode?: boolean; // For signing mode - disables drag/resize/delete
  };
  // Form field overlay props (for edit mode)
  formFieldOverlayProps?: {
    formFields: any[];
    fieldValues: Record<string, any>;
    onUpdateValue: (fieldId: string, value: any) => void;
    readOnly?: boolean;
  };
  // Document editor toolbar props (for edit mode)
  resourceType?: ResourceType; // 'document' or 'template' - determines which mutations to use
  documentId?: string;
  teamId?: string; // Required for PDF upload mutations (insert/delete/reorder pages)
  documentTitle?: string;
  lastSaved?: string;
  counterpartyPermissionLevel?: CounterpartyPermissionLevel; // What counterparties can do (shown in dropdown)
  effectivePermissionLevel?: CounterpartyPermissionLevel; // What the CURRENT USER can do (based on team permissions)
  canAccessFormsToggle?: boolean; // Whether to show Annotate/Forms toggle (creator team with edit permission only)
  onChangePermissions?: (level: CounterpartyPermissionLevel) => void;
  canEditPermissions?: boolean;
  // Comments visibility control
  hideComments?: boolean; // Hide comments during signing states (PREPARING_TO_SIGN, SIGNING, EXECUTED)
  // Hide form fields and annotations overlays (for flattened PDFs)
  hideFormFieldsAndAnnotations?: boolean; // Hide FormFieldOverlay, StrikeoutOverlay, FreeTextOverlay for flattened PDFs (PREPARING_TO_SIGN, SIGNING, EXECUTED)
  // Current user information for comments
  currentUserId?: string;
  currentUserName?: string;
  // Saved PDF form fields and annotations from backend
  savedPdfFormFields?: any[];
  savedPdfAnnotations?: any[];
  // Callback when PDF form fields or annotations are saved
  onPdfSaveSuccess?: (timestamp?: string) => void;
}

export interface PDFPageInfo {
  pageNumber: number;
  width: number;
  height: number;
  scale: number;
}

export interface ZoomOptions {
  level?: number; // Specific zoom level (e.g., 1.5 for 150%)
  mode?: 'fitWidth' | 'fitHeight' | 'fitPage'; // Auto-fit modes
}

// ============================================================================
// Unified Form Field Types (Consolidation)
// ============================================================================

/**
 * Unified form field type that handles both:
 * - Embedded PDF form fields (extracted from PDF)
 * - User-created fields (signature, initials, date, text)
 *
 * This replaces the following deprecated types:
 * - BuilderFormField (BuilderFieldsOverlay.tsx)
 * - FormField (useFormFields.ts)
 * - FieldPlacement (useFieldPlacements.ts)
 * - Field (FieldManager.tsx)
 *
 * TWO-PHASE PAGE TRACKING:
 * - pageId: Stable page identity (UUID) - NEVER changes
 * - pageNum: Derived page position - computed from pageId lookup
 */
export interface FormField {
  // Identity
  id: string;
  fieldName?: string; // For embedded PDF fields

  // Type - covers all field types
  type: 'textfield' | 'checkbox' | 'signature' | 'initials' | 'date' | 'text' | 'multiline';

  // Page Identity (Two-Phase Tracking)
  pageId: string;    // Stable page UUID (identity)
  pageNum: number;   // Current page position (derived from pageId)

  // Position (in PDF points, top-left origin)
  x: number;
  y: number;
  width: number;
  height: number;

  // Value
  value?: string | boolean;

  // UI/Display
  placeholder?: string;
  label?: string;
  horizontalAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  multiline?: boolean;
  fontSize?: number;
  fontColor?: string;

  // Metadata
  existing?: boolean; // True if extracted from PDF
  signer?: string; // Signer ID for signing fields
  required?: boolean;
  readOnly?: boolean;
  editable?: boolean;

  // Additional data
  data?: Record<string, any>;
}

/**
 * Field placement structure as stored in backend field_placements JSONB column
 * Maps to the GraphQL FieldPlacementInput type
 */
export interface FieldPlacement {
  id: string;
  type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
  page: number;
  x: number;
  y: number;
  w: number; // Width
  h: number; // Height
  signer?: string; // Signer ID
  req: boolean; // Required
  data?: {
    label?: string;
    color?: string;
    [key: string]: any;
  };
}

/**
 * PDF Comment (sticky note annotation)
 * TWO-PHASE PAGE TRACKING: pageId (stable) + pageNum (derived)
 */
export interface PDFComment {
  id: string;
  pageId: string;    // Stable page UUID
  pageNum: number;   // Derived from pageId
  x: number;
  y: number;
  text: string;
  author: string;
  authorId: string;
  createdAt: string;
  parentId?: string; // For threaded comment replies
}

/**
 * PDF Strikeout markup
 * TWO-PHASE PAGE TRACKING: pageId (stable) + pageNum (derived)
 */
export interface PDFStrikeout {
  id: string;
  pageId: string;    // Stable page UUID
  pageNum: number;   // Derived from pageId
  x: number;
  y: number;
  width: number;
  color?: string;
  groupId?: string;
}

/**
 * PDF Free text annotation
 * TWO-PHASE PAGE TRACKING: pageId (stable) + pageNum (derived)
 */
export interface PDFFreeText {
  id: string;
  pageId: string;    // Stable page UUID
  pageNum: number;   // Derived from pageId
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  fontSize?: number;
  fontColor?: string;
}

// ============================================================================
// Page Reconciliation Utilities (Two-Phase Tracking)
// ============================================================================

/**
 * Reconcile page numbers for all annotations based on current page order
 * This is the SINGLE SOURCE OF TRUTH for deriving pageNum from pageId
 *
 * @param items - Annotations/fields with pageId
 * @param pages - Current page order with stable IDs
 * @returns Updated items with pageNum derived from pageId lookup
 */
export function reconcilePageNumbers<T extends { pageId: string; pageNum: number }>(
  items: T[],
  pages: PDFPageRef[]
): T[] {
  const pageMap = new Map(pages.map(p => [p.id, p.position]));

  // Filter out items from deleted pages, then update pageNum for remaining items
  return items
    .filter(item => pageMap.has(item.pageId)) // Remove items whose page was deleted
    .map(item => ({
      ...item,
      pageNum: pageMap.get(item.pageId)!, // Non-null assertion safe after filter
    }));
}

/**
 * Create initial page refs when PDF is loaded
 * Assigns stable UUIDs to each page
 */
export function createPageRefs(pageCount: number): PDFPageRef[] {
  return Array.from({ length: pageCount }, (_, i) => ({
    id: crypto.randomUUID(),
    position: i + 1,
  }));
}

// ============================================================================
// Conversion Utilities
// ============================================================================

/**
 * Convert FormField to FieldPlacement (for backend save)
 */
export function formFieldToPlacement(field: FormField): FieldPlacement {
  return {
    id: field.id,
    type: field.type === 'textfield' || field.type === 'multiline' ? 'text' : field.type as FieldPlacement['type'],
    page: field.pageNum, // Use current page position
    x: field.x,
    y: field.y,
    w: field.width,
    h: field.height,
    signer: field.signer,
    req: field.required ?? false,
    data: field.data,
  };
}

/**
 * Convert FieldPlacement to FormField (for frontend display)
 * Requires pageId to be provided from context
 */
export function placementToFormField(placement: FieldPlacement, pageId: string): FormField {
  return {
    id: placement.id,
    type: placement.type === 'text' ? 'textfield' : placement.type,
    pageId,           // Bind to page identity
    pageNum: placement.page,  // Initial position
    x: placement.x,
    y: placement.y,
    width: placement.w,
    height: placement.h,
    signer: placement.signer,
    required: placement.req,
    data: placement.data,
    existing: true, // Loaded from backend
  };
}

// ============================================================================
// Deprecated Types (for migration reference)
// ============================================================================

/**
 * @deprecated Use FormField instead
 * Legacy type from BuilderFieldsOverlay.tsx
 */
export type BuilderFormField = FormField;

/**
 * @deprecated Use FieldPlacement instead
 * Legacy type from useFieldPlacements.ts
 */
export type LegacyFieldPlacement = FieldPlacement;

/**
 * @deprecated Use FormField instead
 * Legacy type from FieldManager.tsx
 */
export interface LegacyField {
  id: string;
  type: 'signature' | 'text' | 'date' | 'checkbox' | 'initial';
  page: number;
  position: { x: number; y: number; width: number; height: number };
  value?: any;
  source: 'pdf' | 'custom';
  editable: boolean;
}
